import { useCallback, useEffect, useRef } from 'react';
import { router } from 'expo-router';

import { reloadApp } from '@sg/garnish';

import { useSendLogoutToAuthMachine } from '../../context/AuthMachine';
import { removeTokens } from '../../context/AzureAuth';
import { useCustomer } from '../../context/Customer';
import { useUrqlContext } from '../../context/Urql';
import { useLogoutCustomerMutation } from './graphql/LogoutCustomer.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A simple hook that registers a log-out handler to the Urql context. It may
 * then be used to handle log-out when the user's session is invalidated.
 */
export const useRegisterUrqlLogOutHandler = () => {
  const { registerUrqlLogOutHandler } = useUrqlContext();

  // ─────────────────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const [, logOut] = useLogoutCustomerMutation();
  const sendLogoutAction = useSendLogoutToAuthMachine();

  // ─── Refs ────────────────────────────────────────────────────────────

  const customerIdRef = useRef(customer.id);

  // NOTE: To prevent extra callback recreations.
  customerIdRef.current = customer.id;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const logOutCustomer = useCallback(async () => {
    const customerId = customerIdRef.current;

    if (!customerId) return;

    const { data } = await logOut({ input: { customerId } });

    return data?.logout.__typename === 'LogoutSuccess';
  }, [logOut]);

  const handlePostLogOutState = useCallback(async () => {
    await removeTokens();
    sendLogoutAction();
    router.navigate('/account');
  }, [sendLogoutAction]);

  // ─────────────────────────────────────────────────────────────────────

  const logOutHandler = useCallback(async () => {
    const hasLoggedOut = await logOutCustomer();

    // ─── Fallback State ──────────────────────────────────────────────────

    // NOTE: If the log-out mutation fails, we manually refresh the app
    //       as a fallback.
    //
    //       That will re-fetch the session.

    if (!hasLoggedOut) {
      await reloadApp();

      return;
    }

    await handlePostLogOutState();
  }, [handlePostLogOutState, logOutCustomer]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    registerUrqlLogOutHandler(logOutHandler);
  }, [logOutHandler, registerUrqlLogOutHandler]);
};
